import React from "react";
import { motion } from "framer-motion";
import { Check, Clock, ArrowRight, Sparkles } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DoxMascot } from "@/components/Logo";
import { useDoxPreview } from "@/context/DoxPreviewContext";

/**
 * Morning Briefing — "Morning, Dox. Anything I need to know?"
 * The answer is already prepared.
 */
export default function MorningBriefing({ open, briefing, onDismiss, onPriority }) {
  const { openDoxPreview, available: previewAvailable } = useDoxPreview();
  if (!briefing) return null;
  const c = briefing.completed || {};  const lines = [
    c.organized ? `I organized ${c.organized} document${c.organized === 1 ? "" : "s"}` : null,
    c.renewal_packages ? `I prepared ${c.renewal_packages} filing package${c.renewal_packages === 1 ? "" : "s"}` : null,
    c.verified ? `I verified ${c.verified} certificate${c.verified === 1 ? "" : "s"}` : null,
    c.upcoming_renewals ? `I found ${c.upcoming_renewals} upcoming renewal${c.upcoming_renewals === 1 ? "" : "s"}` : null,
    c.needs_you ? `I flagged ${c.needs_you} document${c.needs_you === 1 ? "" : "s"} that need${c.needs_you === 1 ? "s" : ""} your eyes` : null,
  ].filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onDismiss()}>
      <DialogContent className="max-w-lg glass-strong border-white/10 text-white overflow-hidden p-0" data-testid="morning-briefing">
        <div className="relative p-8">
          <div className="glow-orb w-64 h-64 -right-16 -top-16 bg-dox-orange/20" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-6">
              <div className="animate-float"><DoxMascot size={52} /></div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-dox-orange font-bold flex items-center gap-1.5">
                  <Sparkles size={12} /> Morning Briefing
                </div>
                <h2 className="font-display font-black text-2xl mt-1">Good morning, {briefing.name}.</h2>
              </div>
            </div>

            <p className="text-white/55 text-sm mb-3">While you were away, I:</p>
            <div className="space-y-2.5 mb-6">
              {lines.length === 0 && <p className="text-white/40 text-sm">Everything's already under control.</p>}
              {lines.map((l, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <span className="h-6 w-6 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center shrink-0">
                    <Check size={13} />
                  </span>
                  <span className="text-white/85 text-sm">{l}</span>
                </motion.div>
              ))}
            </div>

            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 flex items-center gap-3 mb-5">
              <Clock size={18} className="text-dox-magenta shrink-0" />
              <div>
                <div className="text-xs text-white/45">Estimated administrative time saved</div>
                <div className="font-display font-black text-xl text-gradient-brand">{briefing.time_saved_label}</div>
              </div>
            </div>

            {briefing.priority ? (
              <div className="rounded-xl border border-dox-orange/30 bg-dox-orange/[0.06] p-4 mb-6">
                <div className="text-xs uppercase tracking-wider text-dox-orange font-bold mb-1">Today's priority</div>
                <div className="text-white font-medium">{briefing.priority.text}</div>
                {briefing.priority.fee && briefing.priority.fee !== "None" && briefing.priority.fee !== "Unknown" && (
                  <div className="text-white/50 text-sm mt-1">Government fee: {briefing.priority.fee}</div>
                )}
                {previewAvailable && (
                  <button
                    data-testid="briefing-preview-btn"
                    onClick={() => { onDismiss(); openDoxPreview(); }}
                    className="mt-3 text-sm font-semibold text-dox-orange hover:text-dox-orangeHover inline-flex items-center gap-1.5 transition-colors"
                  >
                    Preview what {"I'll"} do <ArrowRight size={14} />
                  </button>
                )}
              </div>
            ) : briefing.next_renewal ? (
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 mb-6" data-testid="briefing-next-renewal">
                <div className="text-xs uppercase tracking-wider text-white/40 font-bold mb-1">Coming up</div>
                <div className="text-white font-medium">
                  I found your {briefing.next_renewal.classification} renewal
                  {briefing.next_renewal.expires_on ? <> — <span className="text-white/70">{formatShortDate(briefing.next_renewal.expires_on)}</span></> : null}
                  {briefing.next_renewal.authority ? <span className="text-white/50"> · {briefing.next_renewal.authority}</span> : null}.
                </div>
                {previewAvailable && (
                  <button
                    data-testid="briefing-preview-btn"
                    onClick={() => { onDismiss(); openDoxPreview(); }}
                    className="mt-3 text-sm font-semibold text-dox-orange hover:text-dox-orangeHover inline-flex items-center gap-1.5 transition-colors"
                  >
                    Show me how {"you'll"} handle it <ArrowRight size={14} />
                  </button>
                )}
              </div>
            ) : (
              <p className="text-white/60 text-sm mb-6">Everything else is under control.</p>
            )}

            <div className="flex gap-3">
              {briefing.priority && (
                <button
                  data-testid="briefing-priority-btn"
                  onClick={() => onPriority(briefing.priority.doc_id)}
                  className="flex-1 rounded-full bg-dox-orange py-3 font-bold text-white flex items-center justify-center gap-2 hover:bg-dox-orangeHover transition-colors"
                >
                  Review priority <ArrowRight size={16} />
                </button>
              )}
              <button
                data-testid="briefing-dismiss-btn"
                onClick={onDismiss}
                className={`rounded-full glass py-3 font-semibold text-white hover:bg-white/10 transition-colors ${briefing.priority ? "px-5" : "flex-1"}`}
              >
                Start my day
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatShortDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}
